'use client';

import { useEffect, useRef, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosGuide, setIosGuide] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in navigator && (navigator as unknown as { standalone: boolean }).standalone);
    if (isStandalone) return;

    const dismissed = localStorage.getItem('install-dismissed');
    if (dismissed === 'true') return;

    if (isIos()) {
      setVisible(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    if (!iosGuide) return;
    function handleClickOutside(e: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setIosGuide(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [iosGuide]);

  async function handleClick() {
    trackEvent('Install App Clicked', { platform: isIos() ? 'ios' : 'native' });
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      trackEvent(
        outcome === 'accepted' ? 'Install App Accepted' : 'Install App Dismissed',
      );
      if (outcome === 'dismissed') {
        localStorage.setItem('install-dismissed', 'true');
      }
      setDeferredPrompt(null);
      setVisible(false);
      return;
    }

    if (isIos()) {
      setIosGuide((v) => !v);
      return;
    }
  }

  function dismiss() {
    localStorage.setItem('install-dismissed', 'true');
    setVisible(false);
    setIosGuide(false);
  }

  if (!visible) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        title="Instalar Oro Express Academy en tu dispositivo"
        className="group relative flex h-9 touch-manipulation select-none items-center gap-1.5 overflow-hidden rounded-lg border border-gold-500/40 bg-gradient-to-br from-gold-500/15 via-gold-500/10 to-gold-500/5 px-3 text-xs font-semibold text-gold-200 shadow-sm shadow-gold-500/10 transition-all hover:border-gold-400/60 hover:from-gold-500/25 hover:to-gold-500/10 hover:text-gold-100 active:scale-95"
      >
        <Download className="h-3.5 w-3.5 shrink-0" />
        <span className="whitespace-nowrap">Instalar app</span>
        <span className="hidden rounded-full bg-gold-500/25 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gold-100 sm:inline">
          Gratis
        </span>
      </button>

      {iosGuide && (
        <div
          ref={guideRef}
          style={{ backgroundColor: '#0a0e1a' }}
          className="absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-gold-500/40 p-4 shadow-2xl ring-1 ring-black/40"
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <p className="text-sm font-bold text-white">
              Instalar en iPad / iPhone
            </p>
            <button
              type="button"
              onClick={dismiss}
              className="-mr-1 -mt-1 rounded p-1 text-white/60 hover:bg-white/10 hover:text-white"
              aria-label="Cerrar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <ol className="space-y-3 text-sm leading-relaxed text-white/95">
            <li className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold-500 text-[11px] font-bold text-navy-950">
                1
              </span>
              <span>
                Pulsa el icono{' '}
                <Share className="inline h-4 w-4 text-gold-300" />{' '}
                <strong className="font-semibold text-gold-200">Compartir</strong>{' '}
                en la barra de Safari
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold-500 text-[11px] font-bold text-navy-950">
                2
              </span>
              <span>
                Desplázate y selecciona{' '}
                <strong className="font-semibold text-gold-200">
                  "Añadir a pantalla de inicio"
                </strong>
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold-500 text-[11px] font-bold text-navy-950">
                3
              </span>
              <span>
                Confirma con{' '}
                <strong className="font-semibold text-gold-200">"Añadir"</strong>{' '}
                — se creará un icono en tu pantalla de inicio
              </span>
            </li>
          </ol>
          <button
            type="button"
            onClick={dismiss}
            className="mt-4 w-full rounded-lg border border-white/15 py-2 text-xs font-medium text-white/70 transition-colors hover:border-white/30 hover:text-white"
          >
            No mostrar de nuevo
          </button>
        </div>
      )}
    </div>
  );
}
